import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { map } from 'rxjs/operators'
import { SourceKey } from '../models/source-key.model'
import { WorkbenchDataSource } from './workbench-data-source'
import { WorkbenchStoreService } from './workbench-store.service'

/**
 * Targeted match refresh: given the row ids a Flex position update event named, re-read exactly
 * those of them that are actually rendered and fold the server's answer onto the existing row
 * objects.
 *
 * A strict delta, deliberately:
 *  - ids the named source has not loaded are dropped before the request, so an event about rows this
 *    panel is not showing costs no HTTP call at all;
 *  - rows the event did NOT name are never touched, and in particular never cleared — the event
 *    describes what changed, not the complete set of matched rows;
 *  - nothing local is faked. The row's status and counts move only to whatever the re-read reports,
 *    including back to UNMATCHED, because {@link WorkbenchStoreService.patchMatchColumns} writes
 *    only the server fields and never a `group`.
 *
 * Panel-scoped, alongside the store it patches.
 */
@Injectable()
export class MatchRefreshService {
  constructor(
    private readonly store: WorkbenchStoreService,
    private readonly dataSource: WorkbenchDataSource,
  ) {}

  /**
   * Re-read and patch the named rows of one source.
   *
   * @param source the grid source the ids belong to (derived from WHICH event arrived, not from the
   *               ids themselves — a position id alone does not identify its feed)
   * @param rowIds the ids the event named; unknown ones are no-ops
   */
  refresh(source: SourceKey, rowIds: string[]): Observable<void> {
    const wanted = this.rendered(source, rowIds)
    // No fetch for an event that names nothing this grid holds. Not an error: the backend broadcasts
    // per session, so an id belonging to another panel's page is entirely expected.
    if (!wanted.length) return of(undefined)

    return this.dataSource.loadByIds(source, wanted).pipe(
      map(rows => {
        this.store.patchMatchColumns(source, rows)
      }),
    )
  }

  /** The named ids this source currently has rows for, de-duplicated and order-preserving. */
  private rendered(source: SourceKey, rowIds: string[]): string[] {
    const loaded = new Set(
      this.store.rowsForSource(source).map(row => row.rowId),
    )
    const seen = new Set<string>()
    return rowIds.filter(id => {
      if (!loaded.has(id) || seen.has(id)) return false
      seen.add(id)
      return true
    })
  }
}
